import {
  BranchesService,
  BranchShiftsService,
  DistrictsService,
  ExternalBranchesService,
  type BranchAdminListedDtoAdmin,
  type BranchShiftDaySigmaRequest,
  type BranchTagAdminListedDtoAdmin,
  type BranchUserSigmaDto,
  type CategoryAdminListedDtoAdmin,
  type DistrictRegionAdminDto,
  type DistrictsByRegionDistrictAdminDto,
  type PostBranchRequestAdmin,
  type PutBranchRequestAdmin
} from '@/backend/market'
import { BranchRepository } from '../../domain/repositories/BranchRepository'
import { BranchModel } from '../models/BranchModel'
import type { BranchFilterEntity } from '../../domain/entities/BranchFilterEntity'
import { BranchFilterModel } from '../models/BranchFilterModel'
import { BranchMiniModel } from '../models/BranchMiniModel'
import type { BranchMiniEntity } from '../../domain/entities/BranchMiniEntity'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import type { CreateUpdateBranchEntity } from '../../domain/entities/CreateUpdateBranchEntity'
import { CreateUpdateBranchModel } from '../models/CreateUpdateBranchModel'
import { CategoryMiniModel } from '@/features/category/data/models/CategoryMiniModel'
import type { CategoryMiniEntity } from '@/features/category/domain/entities/CategoryMiniEntity'
import { UserMiniEntity } from '@/features/user/domain/entities/UserMiniEntity'
import { UserMiniModel } from '@/features/user/data/models/UserMiniModel'
import type { BranchStatus } from '../../domain/enums/BranchStatus'
import type { DistrictMiniEntity } from '../../domain/entities/DistrictMiniEntity'
import { DistrictMiniModel } from '../models/DistrictMiniModel'
import { DistrictModel } from '../models/DistrictModel'
import type { BranchEntity } from '../../domain/entities/BranchEntity'
import { BranchStatusLogModel } from '../models/BranchStatusLogModel'
import type { BranchStatusLogEntity } from '../../domain/entities/BranchStatusLogEntity'
import type { BranchStatusLogFilterEntity } from '../../domain/entities/BranchSTatusLogFilterEntity'
import { BranchStatusLogFilterModel } from '../models/BranchStatusLogFilterModel'
import type { BranchTagsMiniEntity } from '@/features/branchTags/domain/entities/BranchTagsMiniEntity'
import { BranchTagsMiniModel } from '@/features/branchTags/data/models/BranchTagsMiniModel'
import { BranchPeriodModel } from '../models/BranchPeriodModel'
import type { BranchShiftDayTimeSlotEntity } from '../../domain/entities/BranchShiftDayTimeSlotEntity'
import moment from 'moment'
import type { BranchShiftEntity } from '../../domain/entities/BranchShiftEntity'
import type { BranchLoyaltySettingEntity } from '../../domain/entities/BranchLoyaltySettingEntity'

export class BranchRepositoryImpl implements BranchRepository {
  async getBranches(
    filterData: BranchFilterEntity
  ): Promise<PaginateEntity<BranchMiniEntity[]> | { error: true }> {
    const branchFilterModel = new BranchFilterModel(filterData)
    try {
      const response = await BranchesService.getBranchesAdmin(branchFilterModel)
      const branchMiniModels = response.items?.map(
        (item: BranchAdminListedDtoAdmin) => new BranchMiniModel(item)
      )
      const branchResponse = new PaginateEntity<BranchMiniModel[]>(response, branchMiniModels)
      return branchResponse
    } catch {
      return { error: true }
    }
  }
  async getBranchById(id: number): Promise<BranchEntity | { error: true }> {
    try {
      const response = await BranchesService.getBranchAdmin({ id })
      const branchModel = new BranchModel(response)
      return branchModel
    } catch {
      return { error: true }
    }
  }
  async getBranchCategories(branchId: number): Promise<CategoryMiniEntity[] | { error: true }> {
    try {
      const response = await BranchesService.getBranchCategoriesAdmin({ branchId })
      const categoryMiniModels = response.items?.map(
        (item: CategoryAdminListedDtoAdmin) => new CategoryMiniModel(item as CategoryMiniEntity)
      )
      return categoryMiniModels ?? []
    } catch {
      return { error: true }
    }
  }
  async getBranchUsers(branchId: number): Promise<UserMiniEntity[] | { error: true }> {
    try {
      const response = await BranchesService.getBranchUsersAdmin({ branchId })
      const userMiniModels = response.items?.map(
        (item: BranchUserSigmaDto) => new UserMiniModel(item.user as UserMiniEntity)
      )
      return userMiniModels ?? []
    } catch {
      return { error: true }
    }
  }

  async getBranchShifts(branchId: number): Promise<BranchShiftEntity[] | { error: true }> {
    try {
      const response = await BranchShiftsService.getBranchShiftsAdmin({
        branchId
      })
      return response.branchShiftDays
        ? response.branchShiftDays.map((shift) => {
            return {
              dayOfWeek: shift.dayOfWeek,
              overallShiftStatus: shift.overallShiftStatus,
              periods: shift.periods?.map((period) => {
                return {
                  startTime: period.startTime
                    ? moment.utc(period.startTime, 'HH:mm:ss').local().format('HH:mm')
                    : undefined,
                  endTime: period.endTime
                    ? moment.utc(period.endTime, 'HH:mm:ss').local().format('HH:mm')
                    : undefined,
                  status: period.status,
                  startTimeDayOfWeek: period.startTimeDayOfWeek,
                  endTimeDayOfWeek: period.endTimeDayOfWeek,
                  id: period.id
                }
              })
            }
          })
        : []
    } catch {
      return { error: true }
    }
  }
  async updateBranchShift(
    branchId: number,
    branchShiftData: BranchShiftEntity[]
  ): Promise<BranchShiftEntity[] | { error: true }> {
    const shifts = branchShiftData.filter((shift) => shift.periods && shift.periods?.length > 0)
    const branchShiftModel = shifts.map((shift) => {
      return {
        dayOfWeek: shift.dayOfWeek,
        overallShiftStatus: shift.overallShiftStatus,
        periods: shift.periods?.map((period: BranchShiftDayTimeSlotEntity) => {
          if (!period.startTimeDayOfWeek) {
            period.startTimeDayOfWeek = shift.dayOfWeek
          }
          if (!period.endTimeDayOfWeek) {
            period.endTimeDayOfWeek = shift.dayOfWeek
          }
          return new BranchPeriodModel(period)
        })
      }
    })
    try {
      const response = await BranchShiftsService.putBranchShiftsAdmin({
        branchId: branchId,
        requestBody: {
          branchShiftDayRequests: branchShiftModel as BranchShiftDaySigmaRequest[]
        }
      })

      return response.showcaseShifts ?? []
    } catch {
      return { error: true }
    }
  }
  async createBranch(
    branchData: CreateUpdateBranchEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateBranchModel = new CreateUpdateBranchModel(branchData)
    try {
      await BranchesService.postBranchAdmin({
        requestBody: createUpdateBranchModel as PostBranchRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateBranch(
    id: number,
    branchData: CreateUpdateBranchEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateBranchModel = new CreateUpdateBranchModel(branchData)
    try {
      await BranchesService.putBranchAdmin({
        id,
        requestBody: createUpdateBranchModel as unknown as PutBranchRequestAdmin
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async updateBranchVip(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await BranchesService.putBranchVipAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async updateBranchUser(
    branchId: number,
    branchData: CreateUpdateBranchEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateBranchModel = new CreateUpdateBranchModel(branchData)
    try {
      await BranchesService.putBranchUsersAdmin({
        branchId,
        requestBody: {
          userIds: createUpdateBranchModel.userIds
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async deleteBranch(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await BranchesService.deleteBranchAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async disableBranch(id: number, reasonId: number): Promise<{ success: true } | { error: true }> {
    try {
      await BranchesService.postDisableBranchAdmin({
        id,
        requestBody: {
          disableBranchReasonId: reasonId
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async enableBranch(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await BranchesService.postEnableBranchAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateBranchStatus(
    status: BranchStatus | undefined,
    id: number,
    expireDate: string
  ): Promise<{ success: true } | { error: true }> {
    try {
      await BranchesService.putBranchStatusAdmin({
        id,
        requestBody: {
          manualStatus: status,
          manualStatusExpiresAt: expireDate
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getAllDistricts(regionId: number): Promise<DistrictMiniEntity[] | { error: true }> {
    try {
      const response = await DistrictsService.getDistrictsByRegionAdmin({
        regionId: regionId
      })

      const districtModels = response.districts?.map(
        (district: DistrictsByRegionDistrictAdminDto) =>
          new DistrictModel(district as DistrictRegionAdminDto)
      )

      return districtModels ?? []
    } catch {
      return { error: true }
    }
  }

  async getBranchDistrict(branchId: number): Promise<DistrictMiniEntity[] | { error: true }> {
    try {
      const response = await BranchesService.getBranchDistrictsAdmin({ id: branchId })
      const districtMiniModels = response.districts?.map(
        (district: DistrictsByRegionDistrictAdminDto) => new DistrictMiniModel(district)
      )
      return districtMiniModels ?? []
    } catch {
      return { error: true }
    }
  }

  async updateBranchDistrict(
    branchId: number,
    districtIds: number[]
  ): Promise<{ success: true } | { error: true }> {
    try {
      await BranchesService.putBranchDistrictsAdmin({
        id: branchId,
        requestBody: {
          districtIds
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getBranchStatusLog(
    filterData: BranchStatusLogFilterEntity
  ): Promise<PaginateEntity<BranchStatusLogEntity[]> | { error: true }> {
    const branchStatusLogFilterModel = new BranchStatusLogFilterModel(filterData)
    try {
      const response = await BranchesService.getBranchStatusLogsAdmin(branchStatusLogFilterModel)
      const branchStatusLogModels = response.items?.map(
        (item: BranchStatusLogEntity) => new BranchStatusLogModel(item)
      )
      const branchStatusLogResponse = new PaginateEntity<BranchStatusLogModel[]>(
        response,
        branchStatusLogModels
      )
      return branchStatusLogResponse
    } catch {
      return { error: true }
    }
  }
  async synBranches(): Promise<{ success: true } | { error: true }> {
    try {
      await BranchesService.syncBranchesAdmin()
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async exportFile(branchId: number): Promise<{ file?: Blob | null } | { error: true }> {
    try {
      const response = await BranchesService.exportBranchesAdmin({
        branchId
      })
      return response
    } catch {
      return { error: true }
    }
  }
  async importFile(branchId: number, file: Blob): Promise<{ success: true } | { error: true }> {
    try {
      await BranchesService.importBranchesAdmin({
        branchId,
        formData: {
          file
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async getBranchTags(branchId: number): Promise<BranchTagsMiniEntity[] | { error: true }> {
    try {
      const response = await BranchesService.getBranchBranchTagsAdmin({ branchId })
      const branchTagMiniModels = response.branchTags?.map(
        (item: BranchTagAdminListedDtoAdmin) =>
          new BranchTagsMiniModel(item as BranchTagsMiniEntity)
      )
      return branchTagMiniModels ?? []
    } catch {
      return { error: true }
    }
  }

  async updateBranchTags(
    branchId: number,
    branchData: CreateUpdateBranchEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateBranchModel = new CreateUpdateBranchModel(
      branchData as CreateUpdateBranchEntity
    )
    try {
      await BranchesService.putBranchBranchTagsAdmin({
        branchId: branchId,
        requestBody: {
          branchTagIds: createUpdateBranchModel.branchTagIds
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async deleteBranchMenus(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await BranchesService.deleteBranchMenuAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async syncBranchExternalItems(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await ExternalBranchesService.postSyncExternalBranchItemsAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }

  async updateBranchLoyaltyProgramSetting(
    branchId: number,
    branchLoyaltySetting: BranchLoyaltySettingEntity
  ): Promise<{ success: true } | { error: true }> {
    try {
      await BranchesService.upsertBranchLoyaltyProgramSettingAdmin({
        branchId,
        requestBody: branchLoyaltySetting
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async deleteBranchLoyaltyProgramSetting(
    branchId: number
  ): Promise<{ success: true } | { error: true }> {
    try {
      await BranchesService.deleteBranchLoyaltyProgramSettingAdmin({
        branchId
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
}
